import type {
    CompositionComponent,
    ExtendedMaterial,
    ExtendedMaterialOnTheFly,
    Material,
    MaterialOnTheFly,
} from '../gql';
import type { CollapsedSubcategoryPlaceholder } from '../components/MaterialAutocomplete/utils';
import type { AllMaterialTypes } from './material-types';

import { isDefined } from './filter-util';
import {
    isExtendedMaterial,
    isMaterial,
    isMaterialOnTheFly,
} from './material-types';

/**
 * @description This function takes an array of qualities and converts them into a compositionLabel
 */

const getMaterialOnTheFlyCompositionLabel = (
    materialOnTheFly: ExtendedMaterialOnTheFly | MaterialOnTheFly
): string =>
    materialOnTheFly.compositions
        .filter(isDefined)
        .toSorted((a, b) => {
            if (a === null) return 1; // Handle null case for 'a'
            if (b === null) return -1; // Handle null case for 'b'
            const result = b.percentage - a.percentage;
            if (result === 0)
                return a.rawMaterial.rawMaterialName.localeCompare(
                    b.rawMaterial.rawMaterialName
                ); // Fallback to alphabetical name if percentage is the same
            return result; // Ensure the biggest percentage is first
        })
        .map((composition) =>
            composition
                ? `${composition.percentage}% ${composition.rawMaterial.rawMaterialName}`
                : ''
        ) // First percentage, then rawMaterialName
        .join(', ');

/**
 * @description This function generates a header value for the material provided
 */
const getMaterialHeader = (material: ExtendedMaterial | Material): string => {
    const materialSubcategoryName =
        material.materialSubcategory.materialSubcategoryName;
    const includeSeparator =
        materialSubcategoryName && material.designAttribute;
    return `${materialSubcategoryName}${includeSeparator ? ' • ' : ''}${material.designAttribute ?? ''}`;
};

/**
 * @description This function generates a header value for each composition component inside a material
 */
const getCompositionComponentHeader = (
    compositionComponent: CompositionComponent
): string => compositionComponent.type.replaceAll('_', ' ');

/**
 * @description This function generates the title label of a material option
 * @param material The material to use
 * @returns A string that represents the title label
 */
const getMaterialTitle = (material: ExtendedMaterial | Material): string =>
    `${material.materialNumber} | ${material.libraryRef}`;

/**
 * @description This function generates the description label of a material option
 * @param material The material to use
 * @returns A string that represents the description label
 */

const getMaterialCompositionLabel = (
    material: ExtendedMaterial | Material
): string => {
    //materialComposition only exists on Material
    if (
        'materialComposition' in material &&
        Array.isArray(material.materialComposition)
    ) {
        // we display the first composition component
        return material.materialComposition[0]?.composition?.trim() ?? '';
    }
    // composition only exists on ext composition
    return 'composition' in material
        ? (material.composition?.trim() ?? '')
        : '';
};

const getMaterialDescription = (
    material: ExtendedMaterial | Material
): string => {
    const compositionLabel = getMaterialCompositionLabel(material);

    const manufactureDetailLabel =
        'manufactureDetail' in material &&
        !!material.manufactureDetail?.manufactureDetailName
            ? material.manufactureDetail.manufactureDetailName
            : '';

    const weightLabel =
        !material.weight.length ||
        material.weight.toUpperCase().trim() === 'N/A'
            ? ''
            : material.weight;

    return [
        [compositionLabel, weightLabel].filter(Boolean).join(' • '),
        manufactureDetailLabel,
    ]
        .filter(Boolean)
        .join(' | ');
};

/**
 * @description This function generates the title label of a material on the fly option
 * @param materialOnTheFly The material on the fly to use
 * @returns A string that represents the title label
 */
const getMaterialOnTheFlyTitle = (
    materialOnTheFly: ExtendedMaterialOnTheFly | MaterialOnTheFly
): string => {
    // For BOM
    if (isMaterialOnTheFly(materialOnTheFly)) return 'Material on the fly';

    // For extended composition
    return `${materialOnTheFly.manufactureDetail.manufactureDetailName} (Material on the fly)`;
};

/**
 * @description This function generates the description label of a material on the fly option
 * @param materialOnTheFly The material on the fly to use
 * @returns A string that represents the description label
 */
const getMaterialOnTheFlyDescription = (
    materialOnTheFly: ExtendedMaterialOnTheFly | MaterialOnTheFly
): string => {
    // For BOM
    if (isMaterialOnTheFly(materialOnTheFly)) {
        const compositionLabel =
            getMaterialOnTheFlyCompositionLabel(materialOnTheFly);
        const hasWeight =
            materialOnTheFly.weightValue !== null &&
            !!materialOnTheFly.weightUnit?.symbol?.trim() &&
            !!materialOnTheFly.weightMeasure?.symbol?.trim();
        const weightLabel = !hasWeight
            ? ''
            : `${materialOnTheFly.weightValue} ${
                  materialOnTheFly.weightUnit?.symbol ?? '?'
              }/${materialOnTheFly.weightMeasure?.symbol ?? '?'}`;
        const manufactureName =
            materialOnTheFly.manufactureDetail?.manufacture?.manufactureName ??
            '';
        const manufactureDetailName =
            materialOnTheFly.manufactureDetail?.manufactureDetailName ?? '';
        const manufactureDetailLabel = [manufactureName, manufactureDetailName]
            .filter(Boolean)
            .join(' \u2022 ');
        // Avoid displaying "•" when weight is empty, or "|" when manufacture detail is empty.
        return [
            [compositionLabel, weightLabel].filter(Boolean).join(' • '),
            manufactureDetailLabel,
        ]
            .filter(Boolean)
            .join(' | ');
    }

    // For extended composition
    return [
        getMaterialOnTheFlyCompositionLabel(materialOnTheFly),
        materialOnTheFly.manufactureDetail.manufactureDetailName,
    ]
        .filter(Boolean)
        .join(' • ');
};

/**
 * @description Builds a single searchable string from all text rendered for a
 * material on the fly option (subcategory header, title, and description).
 * Used to filter MOF options on the frontend by matching any rendered text.
 * @param materialOnTheFly The material on the fly to use
 * @returns A lowercased string containing all rendered text
 */
const getMaterialOnTheFlySearchableText = (
    materialOnTheFly: ExtendedMaterialOnTheFly | MaterialOnTheFly
): string =>
    [
        materialOnTheFly.materialSubcategory.materialSubcategoryName,
        getMaterialOnTheFlyTitle(materialOnTheFly),
        getMaterialOnTheFlyDescription(materialOnTheFly),
    ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

/**
 * This function is meant for the getOptionLabel prop in autocompletes.
 * Materials resolve to libraryRef only, since it is used as the search input value and
 * users cannot search across the '|' separator used by getMaterialTitle.
 */
const getOptionLabelFn = (
    option: AllMaterialTypes | CollapsedSubcategoryPlaceholder
): string => {
    if (
        !option ||
        'nonValueOptionId' in option ||
        'emptySubcategoryPlaceholderId' in option ||
        'collapsedSubcategoryPlaceholderId' in option
    ) {
        return '';
    }
    if (isMaterial(option) || isExtendedMaterial(option))
        return option.libraryRef;
    return getMaterialOnTheFlyDescription(option);
};

export {
    getMaterialCompositionLabel,
    getMaterialDescription,
    getMaterialOnTheFlyCompositionLabel,
    getMaterialOnTheFlyDescription,
    getMaterialOnTheFlySearchableText,
    getMaterialOnTheFlyTitle,
    getMaterialTitle,
    getOptionLabelFn,
    getMaterialHeader,
    getCompositionComponentHeader,
};
